import { Button, DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import SalaryIcon from "../../../assets/icons/SalaryIcon";
import InfoTitle from "../../../components/common/InfoTitle";
import TopCard from "../../../components/common/TopCard";
import EmployeeInfo from "../../../components/Leave/LeaveBalance/EmployeeInfo";
import Loader from "../../../components/ui/Loader";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import {
  useGetEmployeePayReportDetailsQuery,
  useLazyDownloadPayrollPayReportQuery,
} from "../../../features/payroll/payrollApi";
import useError from "../../../hooks/useError";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { getStartAndEndDateInAMonth } from "../../../lib/time";
import { addCommaInNumber } from "../../../utils/number";
import {
  formatSouthAsianWithDecimals,
  formatToTwoDecimals,
} from "../../../utils/utils";

export default function PayrollDetails() {
  const { detailsId } = useParams();
  const navigate = useNavigate();
  const [businessMemberId, setBusinessMemberId] = useState<number | undefined>(
    undefined
  );
  const [month, setMonth] = useState<Dayjs | null>();
  const [downloadPayrollPayReport, { isLoading: isDownloading, data: pdfUrl }] =
    useLazyDownloadPayrollPayReportQuery();

  const { isLoading, isError, error, data } =
    useGetEmployeePayReportDetailsQuery({
      params: {
        id: Number(detailsId),
        ...(month
          ? {
              ...getStartAndEndDateInAMonth(
                new Date(month.format(dbDateFormat))
              ),
            }
          : {}),
        business_member_id: businessMemberId,
      },
    });
  useError(isError, error, "Get payroll details data fail");

  useEffect(() => {
    if (data?.data?.schedule_date) {
      setMonth(dayjs(data?.data?.schedule_date));
    }
  }, [data?.data?.schedule_date]);

  const totalSum = data?.data?.gross_salary_breakdown;

  const netPayable =
    (totalSum?.gross || 0) +
    (totalSum?.addition || 0) -
    (totalSum?.deduction || 0);

  const grossData = data?.data?.salaryBreakDown?.gross || {};

  const dynamicGrossDetails = Object.entries(grossData).map(([key, value]) => ({
    label: formatLabel(key),
    amount: isError
      ? 0
      : addCommaInNumber(formatToTwoDecimals(Number(value) || 0)),
  }));

  function formatLabel(key: string) {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char: any) => char.toUpperCase());
  }

  const salaryDetails = [
    ...dynamicGrossDetails,
    {
      label: "Gross Salary",
      amount: isError
        ? 0
        : formatSouthAsianWithDecimals(
            data?.data?.gross_salary_breakdown?.gross || 0
          ),
    },
    {
      label: "Total Addition",
      amount: isError
        ? 0
        : formatSouthAsianWithDecimals(
            data?.data?.gross_salary_breakdown?.addition || 0
          ),
    },
    {
      label: "Total Deduction",
      amount: isError
        ? 0
        : formatSouthAsianWithDecimals(
            data?.data?.gross_salary_breakdown?.deduction || 0
          ),
    },
    {
      label: "Net Payable",
      amount: isError ? 0 : formatSouthAsianWithDecimals(netPayable),
    },
  ];

  // Pdf Download functionality
  const businessMembersId = data?.data?.businessMemberId;
  const handleDownloadPayrollPayReport = () => {
    downloadPayrollPayReport({
      params: {
        business_member_id: Number(businessMembersId),
        ...(month ? { date: dayjs(month).format("YYYY-MM") } : {}),
      },
    });
  };

  useEffect(() => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "payroll_details.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [pdfUrl]);

  return (
    <SidebarLayout title="Payroll">
      <TopCard
        leftChildren={
          <Button
            className="normal-dark-button"
            icon={<BackButtonIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        }
      >
        <Button
          className="download-button"
          icon={<DownloadIcon />}
          loading={isDownloading}
          onClick={handleDownloadPayrollPayReport}
        >
          Download Payslip
        </Button>
      </TopCard>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="grid md:grid-cols-8 gap-6 mb-6">
          <EmployeeInfo
            name={data?.data?.employeeName}
            email={data?.data?.email}
            department={data?.data?.departmentName}
            designation={data?.data?.designationName}
            joinedDate={
              data?.data?.joinDate
                ? moment(data?.data?.joinDate, dbDateFormat).format(dateFormat)
                : undefined
            }
            image={data?.data?.photo}
            // employeeId={data?.data?.employeeId.toString()}
            employeeId={
              data?.data?.employeeId ? data?.data?.employeeId.toString() : ""
            }
          />
          <div className="col-span-5">
            <div className="bg-white rounded-md border border-extra-light p-6">
              <div className="flex justify-between">
                <div className="flex gap-2">
                  <InfoTitle className="mb-6" icon={<SalaryIcon />}>
                    Salary Details of
                  </InfoTitle>
                </div>
                <div>
                  {/* <DatePicker /> */}
                  <DatePicker
                    format="MMMM YYYY"
                    style={{ width: 160 }}
                    suffixIcon={<DateIcon />}
                    picker="month"
                    onChange={(val) => {
                      setMonth(val);
                      setBusinessMemberId(data?.data?.businessMemberId);
                    }}
                    value={month}
                    className="clear-button-hide"
                  />
                </div>
              </div>

              {salaryDetails?.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between border-b py-2 border-b-[#E9EFF6] last:border-none"
                >
                  <span className="text-[#8897AE]">{item?.label}</span>
                  <span className="font-medium text-[#455468]">
                    {item?.amount || 0} BDT
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
}
